import React, { useEffect, useState, useCallback } from 'react';
import { t } from '@/locales/strings';
import { track } from '@/utils/analytics';
import { isPlayerCoached, markPlayerCoached } from '@/utils/introState';
import useReducedMotion from '@/hooks/useReducedMotion';

/**
 * PlayerCoachOverlay — a one-time floating coaching layer shown on
 * the first session a user plays. The overview RouteHint covers the
 * orientation textually before the player mounts; this overlay
 * lands the same lesson during play, with the visuals actually
 * moving on screen behind it.
 *
 * Three captions cycle in three on-screen positions:
 *   1. centre (the flower)
 *   2. centre line / breath axis (the sage breath line)
 *   3. top-right (the mute / voice toggle in RingPlayer)
 *
 * Captions auto-advance every 4.2s; the user can tap Next to skip
 * ahead or Got it to dismiss the whole layer. Marked done in
 * introState on first dismiss or on the last caption auto-advance.
 *
 * Mounted by RingPlayer once per process. Renders nothing if the
 * user has already seen it, has reduced-motion preferences (we
 * skip the auto-advance entirely and require manual Next), or has
 * disabled it via the reset path in Profile.
 */
export default function PlayerCoachOverlay({ lang = 'en' }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const reducedMotion = useReducedMotion();
  const total = 3;

  useEffect(() => {
    if (!isPlayerCoached()) {
      // Wait a beat so the player itself has rendered before we
      // overlay anything, otherwise the user sees the overlay
      // before the rings.
      const id = setTimeout(() => {
        setOpen(true);
        track('player_coach_shown');
      }, 900);
      return () => clearTimeout(id);
    }
  }, []);

  const dismiss = useCallback(() => {
    markPlayerCoached();
    track('player_coach_dismissed', { step });
    setOpen(false);
  }, [step]);

  const next = useCallback(() => {
    setStep(s => {
      if (s >= total - 1) {
        markPlayerCoached();
        track('player_coach_completed');
        setOpen(false);
        return s;
      }
      return s + 1;
    });
  }, []);

  // Auto-advance — skipped under reduced-motion so screen-reader
  // and motion-sensitive users have time to read each caption.
  useEffect(() => {
    if (!open || reducedMotion) return;
    const id = setTimeout(next, 4200);
    return () => clearTimeout(id);
  }, [open, step, reducedMotion, next]);

  // Esc-to-close — the overlay is dismissable by design and most
  // users on a desktop will reach for Escape first. The forced
  // FirstRunTour gets this for free via useModalA11y; the coach
  // overlay can't reuse that hook because it isn't a modal (the
  // player below stays interactive), so wire the handler directly.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  if (!open) return null;

  // Caption position per step, expressed as { top|bottom, left|right }
  // values that read against the RingPlayer's dark canvas.
  const positions = [
    // Flower — centre, just above the rings
    { top: '36%',    left: '50%', transform: 'translate(-50%, -50%)' },
    // Breath line — centre, on the breath axis
    { top: '54%',    left: '50%', transform: 'translate(-50%, -50%)' },
    // Voice toggle — top-right, anchored under the player's voice button
    { top: 'calc(env(safe-area-inset-top) + 60px)', right: 16 },
  ];
  const pos = positions[step] || {};

  return (
    <div
      // role="region" + aria-label, NOT role="dialog". A dialog
      // implies focus trap and that the rest of the UI is inert;
      // this overlay deliberately keeps the player below
      // interactive (`pointerEvents: 'none'` on the wrapper). A
      // screen reader announcing a non-modal dialog would mislead
      // users into expecting modal semantics. zIndex stays below
      // the player's TopBar (z 100 elsewhere) so the voice toggle
      // remains tappable while the third caption points at it.
      role="region"
      aria-label={t('player_coach_aria', lang)}
      aria-live="polite"
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        // Light backdrop scrim so the player visuals stay visible
        // behind the coaching captions. The user is meant to watch
        // the rings respond to her body while reading.
        background: 'linear-gradient(180deg, rgba(28,14,22,0.18) 0%, rgba(28,14,22,0.42) 100%)',
        pointerEvents: 'none',
        animation: reducedMotion ? 'none' : 'bb-coach-fade 320ms ease-out',
      }}
    >
      {/* Step caption — pointer-events on so it intercepts taps for
          the Next / Got it buttons even though the surrounding
          backdrop is pointer-transparent (we want the player to
          remain interactive underneath the captions). */}
      <div
        key={step}
        style={{
          position: 'absolute',
          ...pos,
          maxWidth: 280,
          minWidth: 220,
          padding: '14px 16px',
          borderRadius: 18,
          background: 'rgba(255,255,255,0.96)',
          boxShadow: '0 18px 40px -16px rgba(0,0,0,0.45)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          pointerEvents: 'auto',
          animation: reducedMotion ? 'none' : 'bb-coach-fade 320ms ease-out',
        }}
      >
        <div style={{
          font: '600 9px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--primary-dark, #a85c4b)',
          marginBottom: 6,
        }}>
          {t(`player_coach_step${step + 1}_eyebrow`, lang)}
        </div>
        <div style={{
          font: '500 16px/1.25 var(--font-title, Fraunces), serif',
          color: 'var(--ink, #2d1f26)',
          marginBottom: 4,
          textWrap: 'balance',
        }}>
          {t(`player_coach_step${step + 1}_title`, lang)}
        </div>
        <div style={{
          font: '400 13px/1.45 var(--font-body, Manrope)',
          color: 'var(--ink-dim, #6b5560)',
          textWrap: 'balance',
          marginBottom: 10,
        }}>
          {t(`player_coach_step${step + 1}_body`, lang)}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 4 }} aria-hidden="true">
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} style={{
                width: i === step ? 14 : 5,
                height: 5,
                borderRadius: 999,
                background: i === step
                  ? 'var(--primary, #c97a68)'
                  : 'color-mix(in srgb, var(--primary, #c97a68) 22%, transparent)',
                transition: 'width 220ms ease',
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={dismiss}
              style={{
                minHeight: 32,
                padding: '0 10px',
                borderRadius: 999,
                border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
                background: 'transparent',
                color: 'var(--ink-dim, #6b5560)',
                font: '600 12px/1 var(--font-ui, Manrope)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {t('player_coach_dismiss', lang)}
            </button>
            <button
              type="button"
              onClick={next}
              style={{
                minHeight: 32,
                padding: '0 14px',
                borderRadius: 999,
                border: 'none',
                background:
                  'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
                color: '#fff',
                font: '600 12px/1 var(--font-ui, Manrope)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {step >= total - 1
                ? t('player_coach_done', lang)
                : t('player_coach_next', lang)}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes bb-coach-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
